import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  heading?: string;
  items?: FAQItem[];
}

const defaultFAQs: FAQItem[] = [
  {
    question: "Who is the best roofing company near me in Orlando?",
    answer: "Thomas Roofing & Repair is Central Florida's top-rated local roofing company, with 500+ roofs completed and a 5-star reputation. We provide fast response, honest inspections, and expert repairs for homeowners throughout Orlando and surrounding counties — Orange, Brevard, Volusia, Seminole, and more.",
  },
  {
    question: "Who provides roof repair near me in Central Florida?",
    answer: "Thomas Roofing & Repair provides local roof repair services with same-day and next-day response times across Central Florida. Homeowners in Orlando, Titusville, Melbourne, Daytona Beach, and surrounding areas choose us for accurate diagnostics and long-lasting repairs.",
  },
  {
    question: "How much does roof repair cost?",
    answer: "Roof repair costs vary based on damage extent, materials needed, and roof size. Minor repairs may range from a few hundred dollars, while larger storm damage repairs cost more. A free professional inspection gives you an accurate estimate with no obligation to proceed.",
  },
  {
    question: "How long does a roof replacement take?",
    answer: "Most residential roof replacements in Central Florida are completed within one to three days, depending on size, roof complexity, and weather. We give you a clear project timeline upfront so there are no surprises. Larger homes or custom materials may require additional time.",
  },
  {
    question: "Should I repair or replace my roof?",
    answer: "If your roof is under 15 years old and damage is localized, repair is usually the smarter investment. Replacement makes more sense for roofs over 20 years old, widespread damage, or repeated leaks. A free inspection from our team gives you an honest recommendation either way.",
  },
  {
    question: "How fast can roof repair be done?",
    answer: "Emergency repairs can often be completed the same day you call. Standard repairs are typically scheduled within 24–48 hours. We prioritize urgent cases like active leaks or storm damage because every hour of delay risks additional water damage inside your Florida home.",
  },
  {
    question: "Are you licensed and insured for roofing in Florida?",
    answer: "Yes. Thomas Roofing & Repair Inc. is fully licensed (License #CCC1333138) and insured in Florida. We carry liability insurance and workers' compensation, protecting both your property and our crew on every job. You can request our documentation at any time before work begins.",
  },
];

export default function FAQSection({
  heading = "Roofing Questions Homeowners Ask",
  items = defaultFAQs,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
            FAQ
          </span>
          <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
            {heading}
          </h2>
          <p className="text-[#6B7280] font-body text-base max-w-xl mx-auto">
            Real answers to the questions Central Florida homeowners ask us most.
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`rounded-lg border transition-all duration-200 ${
                openIndex === idx
                  ? "border-[#10B981]/40 bg-[#F8FDF9]"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              >
                <span className={`font-body font-600 text-sm lg:text-base pr-4 transition-colors ${
                  openIndex === idx ? "text-[#1A1A1A]" : "text-[#1A1A1A]"
                }`}>
                  {item.question}
                </span>
                <div className={`w-7 h-7 flex items-center justify-center shrink-0 rounded-full transition-all duration-200 ${
                  openIndex === idx ? "bg-[#10B981] text-white" : "bg-gray-100 text-[#6B7280]"
                }`}>
                  <i className={`${openIndex === idx ? "ri-subtract-line" : "ri-add-line"} text-sm`}></i>
                </div>
              </button>
              {openIndex === idx && (
                <div className="px-6 pb-6">
                  <p className="text-[#6B7280] font-body text-sm leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
